import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useCallback, useEffect, useMemo, type ReactNode } from "react";

import { api, call, onUnauthorized } from "@/core/api/client";
import { ENDPOINTS } from "@/core/api/endpoints";
import { ApiError } from "@/core/api/errors";
import { QUERY_KEYS } from "@/core/api/query-client";
import { ADMIN_ROLES } from "@/core/constants";

import type { IdentityAdapter } from "./identity";
import { SessionContext, type Me, type SessionState, type SessionStatus } from "./session-context";

export { useSession, type Me } from "./session-context";

/** Who is signed in: GET /v1/me with the session cookie (null when there is no valid session). */
async function loadMe(): Promise<Me | null> {
  try {
    return (await call(api.GET(ENDPOINTS.me))).data;
  } catch (error) {
    if (error instanceof ApiError && error.status === 401) return null;
    throw error;
  }
}

/**
 * Sign-in: the identity provider proves who it is → its fresh ID token goes to POST /v1/auth/session →
 * the API sets the HttpOnly cookie → the provider forgets the sign-in. The browser never keeps a token.
 */
export function SessionProvider({ identity, children }: { identity: IdentityAdapter; children: ReactNode }) {
  const queryClient = useQueryClient();
  const me = useQuery({ queryKey: QUERY_KEYS.me, queryFn: loadMe, staleTime: 5 * 60_000 });

  /**
   * Drops every cached screen's data except the session itself (screens watching "me" stay attached, so
   * they see the change at once).
   */
  const forgetData = useCallback(
    () => queryClient.removeQueries({ predicate: (query) => query.queryKey[0] !== QUERY_KEYS.me[0] }),
    [queryClient],
  );

  // Any 401 later (session expired, signed out elsewhere, suspended): drop everything, back to sign-in.
  useEffect(
    () =>
      onUnauthorized(() => {
        forgetData();
        queryClient.setQueryData(QUERY_KEYS.me, null);
      }),
    [queryClient, forgetData],
  );

  const startSession = useCallback(
    async (getToken: () => Promise<string>) => {
      const idToken = await getToken();
      try {
        await call(api.POST(ENDPOINTS.session, { body: { idToken } }));
      } finally {
        await identity.clear();
      }
      forgetData();
      await queryClient.refetchQueries({ queryKey: QUERY_KEYS.me });
    },
    [identity, queryClient, forgetData],
  );

  const signOut = useCallback(
    async (options?: { everywhere?: boolean }) => {
      try {
        await call(
          api.DELETE(ENDPOINTS.session, {
            params: { query: options?.everywhere ? { everywhere: "true" } : {} },
          }),
        );
      } finally {
        forgetData();
        queryClient.setQueryData(QUERY_KEYS.me, null);
      }
    },
    [queryClient, forgetData],
  );

  const value = useMemo<SessionState>(() => {
    const user = me.data ?? null;
    const status: SessionStatus = me.isPending
      ? "loading"
      : user
        ? "signed-in"
        : me.isError
          ? "unavailable"
          : "signed-out";
    const role = user?.platformRole;
    return {
      status,
      me: user,
      retry: () => void me.refetch(),
      isAdmin: role !== undefined && (ADMIN_ROLES as readonly string[]).includes(role),
      isSuperAdmin: role === "SUPER_ADMIN",
      signInWithGoogle: () => startSession(() => identity.signInWithGoogle()),
      signInWithEmail: (email, password) => startSession(() => identity.signInWithEmail(email, password)),
      sendPasswordReset: (email) => identity.sendPasswordReset(email),
      signOut,
    };
  }, [me, identity, startSession, signOut]);

  return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>;
}
